import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowLeft,
  Coins,
  Loader2,
  Phone,
  Settings as SettingsIcon,
  Timer,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useUserUsage } from '../../hooks/useAdminUsage';
import { useAdminRefundCall } from '../../hooks/useBilling';
import { useToast } from '../../components/ui/toast';
import { HeaderActions } from '../../components/HeaderActions';
import { cn } from '../../lib/utils';

export function AdminUserUsagePage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const detail = useUserUsage(uid);

  if (!uid) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Keine Benutzer-ID übergeben.
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Verbrauchsdaten …
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
        Fehler: {(detail.error as Error)?.message ?? 'Konnte Daten nicht laden.'}
      </div>
    );
  }

  const data = detail.data;
  const history = [...data.history].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
  const currentMonth = data.history[0];
  const total12mCost = data.history.reduce((a, m) => a + m.estimatedCostUsd, 0);
  const total12mMinutes = data.history.reduce((a, m) => a + m.totalMinutes, 0);
  const total12mCalls = data.history.reduce((a, m) => a + m.callCount, 0);

  const chartData = history.map((m) => ({
    label: shortMonthLabel(m.yearMonth),
    yearMonth: m.yearMonth,
    Telefonie: round2(m.breakdown.telephonyUsd),
    LLM: round2(m.breakdown.llmUsd),
    TTS: round2(m.breakdown.ttsUsd),
    STT: round2(m.breakdown.sttUsd),
    minutes: m.totalMinutes,
    calls: m.callCount,
    cost: m.estimatedCostUsd,
    isCurrent: m.yearMonth === currentMonth?.yearMonth,
  }));

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/usage')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Zurück zur Übersicht
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/users/${uid}/settings/provider`}>
            <SettingsIcon className="mr-1.5 h-3.5 w-3.5" /> Einstellungen verwalten
          </Link>
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>{prettyName(data.user)}</CardTitle>
              <CardDescription>{data.user.email}</CardDescription>
            </div>
            <div className="flex flex-wrap gap-1">
              {data.user.role === 'admin' && <Badge variant="warning">Admin</Badge>}
              {data.user.membership?.tier && (
                <Badge variant="outline">{data.user.membership.tier}</Badge>
              )}
              {data.user.membership?.status && (
                <Badge
                  variant={
                    data.user.membership.status === 'active'
                      ? 'success'
                      : data.user.membership.status === 'paused'
                        ? 'warning'
                        : 'destructive'
                  }
                >
                  {data.user.membership.status}
                </Badge>
              )}
              {data.user.disabled && <Badge variant="destructive">gesperrt</Badge>}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          icon={<Coins className="h-4 w-4" />}
          label={`Kosten ${monthLabel(currentMonth?.yearMonth)}`}
          value={formatUsd(currentMonth?.estimatedCostUsd ?? 0)}
          hint={`${formatUsd(total12mCost)} in den letzten 12 Monaten`}
        />
        <SummaryCard
          icon={<Timer className="h-4 w-4" />}
          label="Minuten aktueller Monat"
          value={(currentMonth?.totalMinutes ?? 0).toLocaleString('de-DE', {
            maximumFractionDigits: 1,
          })}
          hint={`Ø ${(total12mMinutes / Math.max(1, data.history.length)).toLocaleString('de-DE', {
            maximumFractionDigits: 0,
          })} Minuten/Monat`}
        />
        <SummaryCard
          icon={<Phone className="h-4 w-4" />}
          label="Anrufe aktueller Monat"
          value={(currentMonth?.callCount ?? 0).toString()}
          hint={`${total12mCalls} Anrufe gesamt (12 Monate)`}
        />
        <SummaryCard
          icon={<Zap className="h-4 w-4" />}
          label="Latenz Ø (P50 / P95)"
          value={`${currentMonth?.avgLatencyP50Ms ?? 0} / ${currentMonth?.avgLatencyP95Ms ?? 0} ms`}
          hint={
            currentMonth?.topModel
              ? `Top-Modell: ${currentMonth.topModel}`
              : 'Keine Daten'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Kostenverlauf · letzte 12 Monate</CardTitle>
          <CardDescription>
            Aufgeschlüsselt nach Telefonie, LLM, TTS und STT. Tarife gültig seit{' '}
            {data.ratesEffectiveDate}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number, name: string) => [formatUsd(value), name]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar dataKey="Telefonie" stackId="cost" fill="#0ea5e9" />
                <Bar dataKey="LLM" stackId="cost" fill="#8b5cf6" />
                <Bar dataKey="TTS" stackId="cost" fill="#10b981" />
                <Bar dataKey="STT" stackId="cost" fill="#f59e0b">
                  {chartData.map((d, i) => (
                    <Cell key={i} fillOpacity={d.isCurrent ? 1 : 0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Monatsverlauf (Tabelle)</CardTitle>
          <CardDescription>
            Jeder Monat zeigt Anrufe, Minuten und geschätzte Kosten — laufender Monat
            wird live berechnet, abgeschlossene Monate sind aus dem Cache.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Monat</TableHead>
                <TableHead className="text-right">Anrufe</TableHead>
                <TableHead className="text-right">Minuten</TableHead>
                <TableHead className="text-right">Latenz P50/P95</TableHead>
                <TableHead className="text-right">Telefonie</TableHead>
                <TableHead className="text-right">LLM</TableHead>
                <TableHead className="text-right">TTS</TableHead>
                <TableHead className="text-right">STT</TableHead>
                <TableHead className="text-right">Gesamt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...data.history].map((m) => (
                <TableRow
                  key={m.yearMonth}
                  className={cn(m.yearMonth === currentMonth?.yearMonth && 'bg-muted/40')}
                >
                  <TableCell className="font-medium">
                    {monthLabel(m.yearMonth)}
                    {m.yearMonth === currentMonth?.yearMonth && (
                      <Badge variant="outline" className="ml-2">
                        aktuell
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{m.callCount}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {m.totalMinutes.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                    {m.avgLatencyP50Ms} / {m.avgLatencyP95Ms} ms
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatUsd(m.breakdown.telephonyUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatUsd(m.breakdown.llmUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatUsd(m.breakdown.ttsUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {formatUsd(m.breakdown.sttUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatUsd(m.estimatedCostUsd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Letzte Anrufe (max. 50)</CardTitle>
          <CardDescription>
            Pro Anruf: Dauer, Modell, Latenz und geschätzte Kosten.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.recent.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Noch keine Anrufe.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Anrufer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead className="text-right">Dauer</TableHead>
                  <TableHead className="text-right">Latenz P95</TableHead>
                  <TableHead>Modell</TableHead>
                  <TableHead className="text-right">Tokens In/Out</TableHead>
                  <TableHead className="text-right">Kosten</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-xs">
                      {c.startedAt
                        ? new Date(c.startedAt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {c.callerNumber ?? '—'}
                    </TableCell>
                    <TableCell>
                      {c.provider ? (
                        <Badge variant="outline">{c.provider}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatDuration(c.durationSec)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {c.latencyP95 ? `${c.latencyP95} ms` : '—'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.llmProviderUsed && c.llmModelUsed
                        ? `${c.llmProviderUsed}/${c.llmModelUsed}`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {c.llmTokensIn.toLocaleString('de-DE')} /{' '}
                      {c.llmTokensOut.toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatUsd(c.cost.totalUsd)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                          <Link to={`/admin/users/${uid}/settings/calls/${c.id}`}>Details</Link>
                        </Button>
                        <RefundCallButton uid={uid!} callId={c.id} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Compact "↩ refund"-button for the per-call admin row. Sums all
 * negative wallet transactions tagged with this call's id and credits
 * them back via `adminRefundCall`. Confirms first because refunds
 * also roll back the spend-cap counter.
 */
function RefundCallButton({ uid, callId }: { uid: string; callId: string }) {
  const refund = useAdminRefundCall();
  const { toast } = useToast();
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 text-xs text-destructive hover:text-destructive"
      onClick={async () => {
        if (!confirm(`Anruf ${callId.slice(0, 8)} erstatten?`)) return;
        try {
          const r = await refund.mutateAsync({ uid, callId });
          toast({
            title: 'Erstattet',
            description: `${(r.refundedCents / 100).toFixed(2)} € wurden gutgeschrieben.`,
          });
        } catch (err) {
          toast({
            title: 'Fehler',
            description: (err as Error).message,
            variant: 'destructive',
          });
        }
      }}
      disabled={refund.isPending}
    >
      {refund.isPending ? <Loader2 className="size-3 animate-spin" /> : '↩ Refund'}
    </Button>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription>{label}</CardDescription>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function prettyName(it: { firstName?: string; lastName?: string; displayName?: string; email?: string }): string {
  const full = [it.firstName, it.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (it.displayName) return it.displayName;
  return it.email ?? '—';
}

function monthLabel(yearMonth: string | undefined): string {
  if (!yearMonth) return '—';
  const [y, m] = yearMonth.split('-').map(Number);
  if (!y || !m) return yearMonth;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric',
  });
}

function shortMonthLabel(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  if (!y || !m) return yearMonth;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'short',
  });
}

function formatUsd(amount: number): string {
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDuration(sec: number): string {
  if (!sec || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
